import { V1Affinity, V1EphemeralContainer, V1HostAlias, V1KeyToPath, V1NodeSelectorRequirement, V1PodAffinityTerm, V1PodDNSConfig, V1PodReadinessGate, V1PodSecurityContext, V1PodTemplateSpec, V1Toleration, V1TopologySpreadConstraint, V1Volume } from "@kubernetes/client-node"
import { splitAndTrimComma } from "yam-operator/util"
import { DiffResult, ExecuteContext, KVString, PlanContext, PlanContextData } from "yam-types"
import { Settings } from "../settings.d"
import { DeployWorkload } from "../types.d"
import ContainerFactory from "./container"

export default class PodTemplateFactory {

  private workloadName: string
  private planContextData: PlanContextData
  private applyCtx: ExecuteContext
  private workloadSpec: DeployWorkload
  private containerFactory: ContainerFactory
  private diff: DiffResult<DeployWorkload>
  private settings: Settings

  constructor(planContext: PlanContext, workloadSpec: DeployWorkload, diff: DiffResult<DeployWorkload>, settings: Settings) {
    this.planContextData = planContext.data
    this.workloadSpec = workloadSpec
    this.workloadName = this.planContextData.workloadName
    this.diff = diff
    this.settings = settings
    this.containerFactory = new ContainerFactory(this.planContextData, workloadSpec, settings)
  }

  generatePodTemplate(applyCtx: ExecuteContext): V1PodTemplateSpec {
    this.applyCtx = applyCtx
    this.containerFactory.setLogger(applyCtx.log)
    const app = this.planContextData.app
    const spec = this.workloadSpec
    const { initContainers, containers } = this.containerFactory.generatePodContainers(this.applyCtx.containerEnvVariables)
    const annotations = {} as KVString
    if (applyCtx.forceRestartWorkload) {
      // if forceRestartWorkload specified, once re-deployed, the Pod should be re-created anyway
      annotations['restartedAt'] = new Date().toISOString()
    }
    const imagePullSecrets = (spec.imagePullSecrets && spec.imagePullSecrets instanceof Array) ? spec.imagePullSecrets :
      splitAndTrimComma(this.settings.imagePullSecrets).concat(splitAndTrimComma(spec.imagePullSecrets)).map(x => { return { name: x.trim() } })
    const disableDnsChange = this.workloadSpec.advancedFeatures?.disableDefaultDNSConfig
    return {
      metadata: {
        labels: Object.assign(
          {},
          applyCtx.k8sClient.getBuiltInLabels(),
          { app, workload: this.workloadName, version: spec.version || '' },
          this.workloadSpec.advancedFeatures?.extraLabels,
        ),
        annotations: Object.assign(annotations, this.workloadSpec.advancedFeatures?.extraAnnotations)
      },
      spec: {
        // yam-engine built-in definitions, extension of native kubernetes API
        initContainers,
        containers,
        volumes: this.generatePodTemplateVolumes(),
        affinity: this.generateAffinitySpec(),
        imagePullSecrets,
        restartPolicy: spec.type.indexOf('Job') !== -1 ? 'OnFailure' : 'Always',
        securityContext: spec.advancedFeatures?.podSecurityContext || this.settings.defaultPodSecurityContext as V1PodSecurityContext,
        serviceAccountName: this.workloadSpec.serviceAccountName || this.planContextData.serviceAccountName,

        // compatible with native kubernetes podTemplate definitions
        hostname: spec.hostname as string,
        hostAliases: spec.hostAliases as V1HostAlias[],
        ephemeralContainers: spec.ephemeralContainers as V1EphemeralContainer[],
        dnsPolicy: spec.dnsPolicy as string || (disableDnsChange ? undefined : this.settings.defaultDnsPolicy) || (spec.advancedFeatures?.hostNetwork ? 'ClusterFirstWithHostNet' : 'ClusterFirst'),
        dnsConfig: spec.dnsConfig as V1PodDNSConfig || (disableDnsChange ? undefined : this.settings.defaultDnsConfig),
        nodeName: spec.nodeName as string,
        overhead: spec.overhead as KVString,
        preemptionPolicy: spec.preemptionPolicy as string,
        priority: spec.priority as number,
        priorityClassName: spec.priorityClassName as string,
        readinessGates: spec.readinessGates as V1PodReadinessGate[],
        activeDeadlineSeconds: spec.activeDeadlineSeconds as number,
        automountServiceAccountToken: spec.automountServiceAccountToken as boolean,
        enableServiceLinks: spec.enableServiceLinks as boolean,
        terminationGracePeriodSeconds: spec.gracefulPeriodSeconds || 30,
        hostIPC: spec.advancedFeatures?.hostIPC,
        hostNetwork: spec.advancedFeatures?.hostNetwork,
        hostPID: spec.advancedFeatures?.hostPID,
        runtimeClassName: spec.runtimeClassName as string,
        schedulerName: spec.schedulerName as string,
        setHostnameAsFQDN: spec.setHostnameAsFQDN as boolean,
        shareProcessNamespace: spec.shareProcessNamespace as boolean,
        subdomain: spec.subdomain as string,
        tolerations: (spec.tolerations || this.settings.defaultTolerations) as V1Toleration[],
        topologySpreadConstraints: (spec.topologySpreadConstraints || this.settings.topologySpreadConstraints) as V1TopologySpreadConstraint[]
      }
    }
  }

  generateAffinitySpec(): V1Affinity {
    if (this.workloadSpec.affinity) {
      return this.workloadSpec.affinity as V1Affinity
    }
    const result = (this.settings.defaultAffinity || {}) as V1Affinity
    // nodeAffinity to enforce pods scheduled to correct node groups
    const selectors = this.workloadSpec.nodeSelector
    if (!this.workloadSpec.advancedFeatures?.disableDefaultNodeAffinity && this.settings.defaultNodeSelectorTerms) {
      // enforce Pod scheduled on correct nodes set by metadata
      const matchExpressions = this.settings.defaultNodeSelectorTerms as V1NodeSelectorRequirement[]
      if (matchExpressions && matchExpressions.length > 0) {
        result.nodeAffinity = {
          requiredDuringSchedulingIgnoredDuringExecution: {
            nodeSelectorTerms: [{
              matchExpressions
            }]
          }
        }
      }
    }
    if (selectors) {
      const selectorKvs = selectors.split(',').map(x => {
        // eslint-disable-next-line prefer-const
        let [key, value] = x.split('=')
        if (!value) {
          value = 'true'
        }
        return {
          key: key.trim(),
          value: value.trim()
        }
      }).reduce((prev, cur) => {
        if ((cur.key in prev) && Array.isArray(prev[cur.key])) {
          prev[cur.key].push(cur.value)
        } else {
          prev[cur.key] = [cur.value]
        }
        return prev
      }, {} as { [key: string]: string[] })

      result.nodeAffinity = {
        requiredDuringSchedulingIgnoredDuringExecution: {
          nodeSelectorTerms: [{
            matchExpressions: Object.keys(selectorKvs).map(key => {
              return {
                key,
                operator: 'In',
                values: selectorKvs[key]
              }
            })
          }]
        }
      }
    }
    // podAntiAffinity to prefer/enforce pods scattered
    if (this.diff.modifiedItems.length > 0) {
      const previousScatterStrategy = this.diff.modifiedItems[0].previous.advancedFeatures?.forceScatterPods
      const currentScatterStrategy = this.diff.modifiedItems[0].current.advancedFeatures?.forceScatterPods
      if (previousScatterStrategy && !currentScatterStrategy) {
        this.applyCtx.log.warn(`change 'forceScatterPods' may not take effect, please edit/replace the 'affinity' block of the deployment directly`)
      }
    }
    const scatter: V1PodAffinityTerm = {
      topologyKey: "kubernetes.io/hostname",
      labelSelector: {
        matchExpressions: [{
          key: 'workload',
          operator: 'In',
          values: [this.workloadName]
        }]
      }
    }
    if (this.workloadSpec.advancedFeatures?.forceScatterPods) {
      result.podAntiAffinity = {
        requiredDuringSchedulingIgnoredDuringExecution: [scatter]
      }
    } else {
      result.podAntiAffinity = {
        preferredDuringSchedulingIgnoredDuringExecution: [{
          podAffinityTerm: scatter,
          weight: 100
        }]
      }
    }
    return result
  }

  generatePodTemplateVolumes(): V1Volume[] {
    const volumes: V1Volume[] = this.workloadSpec.volumes ? (this.workloadSpec.volumes as V1Volume[]) : []
    this.workloadSpec.mounts?.forEach(m => {
      if (m.type === "emptyDir") {
        volumes.push({
          name: m.name,
          emptyDir: {}
        })
      } else if (m.type === "hostPath") {
        volumes.push({
          name: m.name,
          hostPath: {
            path: m.hostPath || m.path,
            type: m.hostPathType || 'DirectoryOrCreate'
          }
        })
      } else if (m.type === "configMap") {
        volumes.push({
          name: m.name,
          configMap: {
            name: m.name,
            // 0o644 is 420 in decimal, which is the default value
            defaultMode: m.defaultMode as number || 0o644,
            items: m.items as V1KeyToPath[],
            optional: m.optional as boolean
          }
        })
      } else if (m.type === "secret") {
        volumes.push({
          name: m.name,
          secret: {
            secretName: m.name,
            // 0o644 is 420 in decimal, which is the default value
            defaultMode: m.defaultMode as number || 0o644,
            items: m.items as V1KeyToPath[],
            optional: m.optional as boolean
          }
        })
      } else if (m.type === "storage" && this.workloadSpec.type !== "StatefulSet") {
        // as for StatefulSet, mounts will be converted into 'volumeMounts' and 'volumeClaimTemplates'
        volumes.push({
          name: m.name,
          persistentVolumeClaim: {
            claimName: this.workloadName + '-' + m.name
          }
        })
      }
    })
    return volumes
  }
}